import { ShoppingCart } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import FeatureHeader from "../components/FeatureHeader";
import Loader from "../components/loader";

export default function AllTests() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);

  const categoryOptions = useMemo(
    () => [
      "All",
      "Blood Tests",
      "Pregnancy & Fertility",
      "Infection & Fever",
      "Diabetes & Glucose",
      "Heart & Cholesterol",
      "Urine Tests",
    ],
    []
  );

  const activeCategory = searchParams.get("category") || "All";

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const testRes = await api.get("/api/labtests");
        if (mounted && testRes?.data?.tests) {
          setTests(testRes.data.tests);
        }
      } catch (err) {
        console.error("Error loading tests:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, []);

  const filteredTests = useMemo(() => {
    if (activeCategory === "All") return tests;

    const normalizedCategory = activeCategory.toLowerCase();
    return tests.filter((test) => {
      const rawCategory = `${test?.category || ""}`.toLowerCase();
      const rawName = `${test?.name || ""}`.toLowerCase();

      if (rawCategory.includes(normalizedCategory)) return true;

      const matchMap = {
        "blood tests": ["blood", "cbc", "pcv", "full blood", "haemat", "hemat"],
        "pregnancy & fertility": ["pregnancy", "fertility", "ovulation", "hcg"],
        "infection & fever": ["infection", "fever", "malaria", "typhoid", "hiv", "hepatitis"],
        "diabetes & glucose": ["diabetes", "glucose", "sugar", "hba1c"],
        "heart & cholesterol": ["heart", "cholesterol", "lipid", "cardiac"],
        "urine tests": ["urine", "urinalysis"],
      };

      return (matchMap[normalizedCategory] || []).some((keyword) => rawCategory.includes(keyword) || rawName.includes(keyword));
    });
  }, [activeCategory, tests]);

  const handleCategoryChange = (category) => {
    setSearchParams(category === "All" ? { category: "All" } : { category });
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "var(--bg)", paddingBottom: 80 }}>
      {/* Header bar */}
    
    <FeatureHeader
      title="All Tests"
      subtitle="Choose any test below to continue."
      onBack={() => navigate(-1)}
    />
      <div style={{ padding: "0 16px", paddingTop: 16, paddingBottom: 80, maxWidth: 640, margin: "0 auto", boxSizing: "border-box" }}>
        <div style={styles.categoryRow}>
          {categoryOptions.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => handleCategoryChange(category)}
              style={{
                ...styles.categoryChip,
                background: activeCategory === category ? "var(--primary)" : "#fff",
                color: activeCategory === category ? "#fff" : "#1f2937",
                border: activeCategory === category ? "1px solid var(--primary)" : "1px solid #e5e7eb",
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {loading && <Loader />}
        {!loading && filteredTests.length === 0 && <p className="text-muted text-center">No tests found.</p>}

        <div style={styles.cardsRow}>
          {filteredTests.map((test) => (
            <div
              key={test._id}
              style={styles.dcard}
              onClick={() => navigate(`/lab-tests/${test._id}`)}
            >
              <div style={styles.imageWrapper}>
                <img src={test.image} alt={test.name} style={styles.productImage} />
                <div style={styles.tag}>Lab Test</div>
                <div style={styles.cartHolder}>
                  <ShoppingCart size={16} color="var(--primary)" />
                </div>
              </div>
              <div style={styles.productInfo}>
                <h6 style={styles.testName}>{test.name}</h6>
                <div style={styles.priceRow}>
                  <span style={styles.oldPrice}>$12.50</span>
                  <span style={styles.newPrice}>${test.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  categoryRow: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 10,
    marginBottom: 12,
  },
  categoryChip: {
    flex: "0 0 auto",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 14px",
    whiteSpace: "nowrap",
    background: "#fff",
    cursor: "pointer",
  },
  cardsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    alignItems: "flex-start",
  },
  dcard: {
    display: "flex",
    flexDirection: "column",
    flex: "0 0 164px",
    minWidth: "164px",
    maxWidth: "164px",
    backgroundColor: "transparent",
    borderRadius: 18,
    padding: 0,
    boxShadow: "none",
    border: "none",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  imageWrapper: {
    position: "relative",
    height: 200,
    overflow: "hidden",
    borderRadius: 18,
    background: "#ededf0",
  },
  productImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  tag: {
    position: "absolute",
    top: 8,
    left: 8,
    fontSize: 9,
    padding: "3px 8px",
    borderRadius: 10,
    backgroundColor: "rgba(106,58,255,0.70)",
    color: "#fff",
    fontWeight: 700,
    zIndex: 2,
  },
  cartHolder: {
    position: "absolute",
    bottom: 8,
    right: 8,
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "#ffffff",
    border: "1.5px solid var(--primary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 20px rgba(0,0,0,0.22)",
  },
  productInfo: {
    marginTop: 6,
    lineHeight: 1.3,
  },
  testName: {
    fontSize: 13,
    fontWeight: 500,
    margin: 0,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "var(--on-bg)",
  },
  priceRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
  },
  oldPrice: {
    fontSize: 11,
    color: "#9b93ab",
    textDecoration: "line-through",
  },
  newPrice: {
    fontSize: 15,
    fontWeight: 800,
    color: "#1f1a28",
  },
};
